import { useMemo, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Inbox } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/Button'
import { AiAnalystModal } from '@/components/AiAnalystModal'
import { DashboardTabControl } from '@/components/Dashboard/DashboardTabControl'
import { DashboardSkeleton } from '@/components/Dashboard/DashboardSkeleton'
import { DashboardToolbarActions } from '@/components/Dashboard/DashboardToolbarActions'
import { DashboardTabContent } from '@/components/Dashboard/DashboardTabContent'
import { buildDashboardTabs, resolveDashboardTab } from '@/components/Dashboard/dashboardTabs'
import { useRolePermissions } from '@/components/Dashboard/RoleGuard'
import {
  DashboardMotionRoot,
  DashboardTabMotion,
} from '@/components/Dashboard/DashboardMotion'
import { mapSummaryData } from '@/components/Dashboard/dashboardPageMappers'
import { DashboardValuesToggle } from '@/components/Dashboard/DashboardValuesToggle'
import { useCrmValuesVisible } from '@/hooks/useCrmValuesVisible'
import { useDashboardData } from '@/hooks/useDashboardData'

export default function Dashboard() {
  const [showAiModal, setShowAiModal] = useState(false)
  const [showPeriods, setShowPeriods] = useState(false)
  const permissions = useRolePermissions()
  const tabs = useMemo(() => buildDashboardTabs(permissions), [permissions])
  const [activeTab, setActiveTab] = useState(() =>
    resolveDashboardTab(permissions.defaultTab, tabs.map((tab) => tab.id)),
  )
  const { valuesVisible, toggleValuesVisibility } = useCrmValuesVisible()

  const { data, loading, error, refresh, period, setPeriod } = useDashboardData('Este mês')
  const entranceKey = `${period}-${loading ? 'loading' : 'ready'}`
  const summaryData = useMemo(() => mapSummaryData(data), [data])

  if (loading) return <DashboardSkeleton />
  if (error) {
    return (
      <DashboardLayout title='Dashboard comercial'>
        <div className='flex flex-col items-center justify-center py-20'>
          <div className='w-16 h-16 rounded-full bg-[var(--glass-bg)] flex items-center justify-center mb-4'>
            <Inbox size={28} className='text-text-muted' />
          </div>
          <h3 className='text-lg font-semibold text-text-primary mb-1'>Erro ao carregar dados</h3>
          <p className='text-sm text-text-muted text-center max-w-xs mb-4'>{error}</p>
          <Button variant='secondary' size='sm' onClick={refresh}>Tentar novamente</Button>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title='Dashboard comercial'>
      <DashboardMotionRoot key={entranceKey} className='flex flex-col gap-6'>
        <div className='flex items-center justify-between gap-3'>
          <div className='min-w-0 flex-1'>
            <DashboardTabControl
              tabs={tabs}
              activeTab={activeTab}
              onChange={setActiveTab}
            />
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            <DashboardToolbarActions
              period={period}
              showPeriods={showPeriods}
              setShowPeriods={setShowPeriods}
              setPeriod={setPeriod}
              onOpenAi={() => setShowAiModal(true)}
            />
            <DashboardValuesToggle
              valuesVisible={valuesVisible}
              onToggle={toggleValuesVisibility}
            />
          </div>
        </div>

        <AnimatePresence mode='wait'>
          <DashboardTabMotion
            motionKey={`${activeTab}-${period}`}
            className='min-h-[200px]'
          >
            <DashboardTabContent
              activeTab={activeTab}
              data={data}
              valuesVisible={valuesVisible}
            />
          </DashboardTabMotion>
        </AnimatePresence>
      </DashboardMotionRoot>

      <AiAnalystModal
        isOpen={showAiModal}
        onClose={() => setShowAiModal(false)}
        tab={activeTab}
        period={period}
        data={summaryData}
      />
    </DashboardLayout>
  )
}
